'use strict';

var typography = require('./typography.js');

/**
 * Generates semantic HTML usage examples for typography classes.
 * @param className - The CSS class name to generate examples for.
 * @param category - The typography category (header or body) to determine appropriate HTML tag.
 * @returns HTML example string with proper semantic tags.
 */ const generateExample = (className, category)=>{
    const tag = category === 'header' ? 'h2' : 'p';
    return `<${tag} className="${className}">Your text content here</${tag}>`;
};
/**
 * Converts theme keys to actual CSS values for display purposes.
 * @param themeKey - The theme key (e.g., 'fontSize.xl', 'fontWeight.circular.bold').
 * @returns The resolved CSS value or the original key if not found in mapping.
 */ const resolveThemeValue = (themeKey)=>{
    // Map theme keys to actual values for documentation
    // Note: This function uses a hardcoded map to resolve theme values.
    // This is a deliberate design choice to make the metadata portable and
    // independent of the build process. If the base theme values in
    // tailwind.config.js are changed, this map must be updated manually to
    // ensure documentation remains accurate.
    const themeValueMap = {
        // Font sizes
        'fontSize.xs': '0.75rem',
        'fontSize.sm': '0.875rem',
        'fontSize.base': '1rem',
        'fontSize.lg': '1.125rem',
        'fontSize.xl': '1.25rem',
        'fontSize.2xl': '1.5rem',
        'fontSize.3xl': '1.875rem',
        'fontSize.4xl': '2.25rem',
        'fontSize.5xl': '3rem',
        // Line heights
        'lineHeight.none': '1',
        'lineHeight.4': '1rem',
        'lineHeight.5': '1.25rem',
        'lineHeight.6': '1.5rem',
        'lineHeight.7': '1.75rem',
        'lineHeight.7.5': '1.875rem',
        'lineHeight.8': '2rem',
        'lineHeight.9': '2.25rem',
        'lineHeight.10': '2.5rem',
        'lineHeight.12': '3rem',
        'lineHeight.15': '3.75rem',
        // Font weights
        'fontWeight.circular.regular': '400',
        'fontWeight.circular.medium': '500',
        'fontWeight.circular.bold': '700',
        // Letter spacing
        'letterSpacing.wider': '0.05em',
        // Colors (simplified for display)
        'textColor.neutral.strong': '#3D3652'
    };
    return themeValueMap[themeKey] ?? themeKey;
};
// Convert token definitions to resolved styles for metadata
const typographyTypes = Object.entries(typography.typographyTokenDefinitions).reduce((acc, [category, variants])=>{
    const resolvedVariants = Object.entries(variants).reduce((variantAcc, [variant, tokenStyles])=>{
        const resolvedStyles = Object.entries(tokenStyles).reduce((styleAcc, [property, value])=>{
            if (typeof value === 'string' && property !== 'textTransform' && property !== 'textDecoration') {
                return {
                    ...styleAcc,
                    [property]: resolveThemeValue(value)
                };
            }
            return {
                ...styleAcc,
                [property]: value
            };
        }, {});
        return {
            ...variantAcc,
            [variant]: resolvedStyles
        };
    }, {});
    return {
        ...acc,
        [category]: resolvedVariants
    };
}, {});
/**
 * Converts typography styles to CSS properties format for PropertyTable.
 * @param styles - The resolved styles object.
 * @returns Record of CSS properties with kebab-case keys.
 */ const stylesToCssProperties = (styles)=>{
    const cssProperties = {
        // Always include font-family
        'font-family': 'CircularXX, sans-serif'
    };
    Object.entries(styles).forEach(([key, value])=>{
        // Convert camelCase to kebab-case
        const cssKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
        cssProperties[cssKey] = String(value);
    });
    return cssProperties;
};
/**
 * Gets typography classes metadata, optionally filtered by category.
 * @param category - Optional category to filter by ('header' | 'body').
 * @returns Array of typography class metadata.
 */ const getTypographyClasses = (category)=>{
    return Object.entries(typographyTypes).filter(([cat])=>!category || cat === category).reduce((acc, [cat, variants])=>{
        const categoryClasses = Object.entries(variants).map(([variant, styles])=>{
            const className = `type-${cat}-${variant}`;
            const displayName = `${cat.charAt(0).toUpperCase() + cat.slice(1)} - ${variant.toUpperCase()}`;
            return {
                className,
                category: cat,
                displayName,
                cssProperties: stylesToCssProperties(styles),
                example: generateExample(className, cat)
            };
        });
        return [
            ...acc,
            ...categoryClasses
        ];
    }, []);
};
// Export all classes grouped by category
const typographyClasses = {
    header: getTypographyClasses('header'),
    body: getTypographyClasses('body')
};

exports.typographyClasses = typographyClasses;
//# sourceMappingURL=typography-meta.js.map
